import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AccessScreen } from './pages/AccessScreen';
import { WelcomeScreen } from './pages/WelcomeScreen';
import { QuizFlow, TOTAL_STEPS } from './pages/QuizFlow';
import { SummaryScreen } from './pages/SummaryScreen';
import { ProcessingScreen } from './pages/ProcessingScreen';
import { FinalScreen } from './pages/FinalScreen';
import { PreparingOverlay } from './components/PreparingOverlay';
import { emptyProfile } from './types/profile';
import type { PersonalizationProfile } from './types/profile';
import { OTHER_INTEREST } from './data/quizOptions';
import { submitPersonalizationProfile } from './utils/submitProfile';
type Stage = 'access' | 'welcome' | 'quiz' | 'summary' | 'processing' | 'final' | 'closed';
export function App() {
  const [stage, setStage] = useState<Stage>('access');
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState<PersonalizationProfile>(emptyProfile);
  const [customInterest, setCustomInterest] = useState('');
  const [preparing, setPreparing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sendDone, setSendDone] = useState(false);
  const [animationDone, setAnimationDone] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [stage, step]);

  // Solo pasamos a la pantalla final cuando el envío terminó
  // y la animación de procesamiento completó su secuencia.
  useEffect(() => {
    if (stage === 'processing' && sendDone && animationDone) {
      setStage('final');
    }
  }, [stage, sendDone, animationDone]);
  const update = useCallback((patch: Partial<PersonalizationProfile>) => {
    setProfile(previous => ({
      ...previous,
      ...patch
    }));
  }, []);
  const handleAnimationDone = useCallback(() => setAnimationDone(true), []);
  const handlePreparingDone = useCallback(() => {
    setPreparing(false);
    setStage('welcome');
  }, []);
  function handleNext() {
    if (step < TOTAL_STEPS) {
      setStep(step + 1);
      return;
    }
    setStage('summary');
  }
  function handleBack() {
    if (step > 1) {
      setStep(step - 1);
      return;
    }
    setStage('welcome');
  }
  async function handleSubmit() {
    if (submitting) return;
    setSubmitting(true);
    setError('');
    setSendDone(false);
    setAnimationDone(false);
    const interests = profile.child_interests.map(item => item === OTHER_INTEREST && customInterest.trim() ? `Algo diferente: ${customInterest.trim()}` : item);

    // Mostramos la animación de procesamiento mientras viaja el POST.
    setStage('processing');
    try {
      await submitPersonalizationProfile({
        ...profile,
        child_interests: interests,
        parental_consent: true
      });
      setSendDone(true);
    } catch (submitError) {
      // Ante un fallo de red volvemos al resumen conservando todas las respuestas.
      setStage('summary');
      setSubmitting(false);
      setError(submitError instanceof Error && submitError.message ? submitError.message : 'No pudimos enviar el perfil. Revisa tu conexión e intenta de nuevo.');
    }
  }
  return <main className="relative flex min-h-screen w-full flex-col overflow-hidden bg-cream" style={{
    paddingTop: 'env(safe-area-inset-top)',
    paddingBottom: 'env(safe-area-inset-bottom)'
  }} data-id="element-0" data-mp="ydqw5ywl8f4c">
      <div aria-hidden="true" className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-lavender-100 blur-3xl" data-id="element-1" data-mp="1w3vl1xr7h526" />
      <div aria-hidden="true" className="pointer-events-none absolute -right-28 top-1/3 h-72 w-72 rounded-full bg-gold-200/40 blur-3xl" data-id="element-2" data-mp="1i5nkzgyvhnms" />

      <div className="relative flex min-h-screen w-full flex-col" data-id="element-3" data-mp="6nmfaa81ea7v">
        <AnimatePresence mode="wait" data-id="element-4" data-mp="apr3uujmognn">
          {stage === 'access' && <AccessScreen key="access" profile={profile} update={update} onSubmit={() => setPreparing(true)} data-id="element-5" data-mp="ano8rw3mbxc2" />}

          {stage === 'welcome' && <WelcomeScreen key="welcome" parentName={profile.parent_name} onStart={() => {
          setStep(1);
          setStage('quiz');
        }} data-id="element-6" data-mp="h6szag5zdd93" />}

          {stage === 'quiz' && <QuizFlow key="quiz" step={step} profile={profile} update={update} customInterest={customInterest} onCustomInterestChange={setCustomInterest} onNext={handleNext} onBack={handleBack} data-id="element-7" data-mp="19tndivc7fge4" />}

          {stage === 'summary' && <SummaryScreen key="summary" profile={profile} customInterest={customInterest} submitting={submitting} error={error} onConsentChange={value => update({
          parental_consent: value
        })} onSubmit={handleSubmit} onEdit={() => {
          setStep(1);
          setStage('quiz');
        }} data-id="element-8" data-mp="3kpm5g7wfizt8" />}

          {stage === 'processing' && <ProcessingScreen key="processing" childName={profile.child_name} onDone={handleAnimationDone} data-id="element-9" data-mp="29xegujxpevrz" />}

          {stage === 'final' && <FinalScreen key="final" childName={profile.child_name} email={profile.purchase_email} onFinish={() => setStage('closed')} data-id="element-10" data-mp="2js6etov77kna" />}

          {stage === 'closed' && <motion.section key="closed" initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4
        }} className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center px-8 text-center" data-id="element-11" data-mp="ti3ebch5g4f7">
              <span aria-hidden="true" className="text-4xl" data-id="element-12" data-mp="1wtspgynleq71">
                💜
              </span>
              <h1 className="mt-4 font-display text-2xl font-semibold text-ink" data-id="element-13" data-mp="2at1tz12frvv6">
                Gracias, {profile.parent_name.trim().split(' ')[0] || 'de corazón'}
              </h1>
              <p className="mt-3 text-[15px] leading-relaxed text-ink-soft" data-id="element-14" data-mp="2kpmyllajn4yh">
                Ya puedes cerrar esta ventana. Te escribiremos a {profile.purchase_email} cuando las
                experiencias de {profile.child_name} estén listas.
              </p>
            </motion.section>}
        </AnimatePresence>

        <AnimatePresence data-id="element-15" data-mp="2phointdydzz2">
          {preparing && <PreparingOverlay key="preparing" onDone={handlePreparingDone} data-id="element-16" data-mp="33gefr90rvznf" />}
        </AnimatePresence>
      </div>
    </main>;
}